import React, { MutableRefObject, useImperativeHandle } from 'react';
import { Nullable } from 'global';
import { Add } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Listing, ListingForm, useListing } from 'components/shared/TableListing';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { StagesFormValues } from '../../../types';
import { AttributeCell, TitleCell } from './cells';
import { stagesConstructorFormRow } from '../../../constants';
import { StagesConstructorListingSchema } from '../../../model';

interface Props {
  isEdit: boolean;
  initialValues: StagesFormValues[];
  submitRef: MutableRefObject<{ submit: () => Promise<Nullable<ListingForm<StagesFormValues>>> } | null>;
}

export const StagesConstructorListing = ({ isEdit, submitRef, initialValues }: Props) => {
  const { form, columns, tableData, handleAddRow, handleRemoveRow, handleMoveRow } = useListing<StagesFormValues>(
    {
      headerColumns: [
        {
          header: 'Stage Name',
          renderKey: 'name',
          renderCell: ({ rowIndex }) => (
            <TitleCell
              isEdit={isEdit}
              rowIndex={rowIndex}
              onMoveRow={handleMoveRow}
              onRemove={() => {
                handleRemoveRow(rowIndex);
              }}
            />
          ),
        },
        {
          header: 'Checklist',
          renderKey: 'checklist',
          renderCell: ({ rowIndex }) => <AttributeCell isEdit={isEdit} rowIndex={rowIndex} prefix="checklist" />,
        },
      ],
      bodyFields: initialValues,
    },
    {
      mode: 'onChange',
      shouldUnregister: false,
      resolver: zodResolver(StagesConstructorListingSchema),
    }
  );

  useImperativeHandle(submitRef, () => ({
    submit: async () => {
      const isValid = await form.trigger();
      return isValid ? form.getValues() : null;
    },
  }));

  return (
    <FormProvider {...form}>
      <Listing<StagesFormValues>
        columns={columns}
        tableData={tableData}
        actionPanelBottom={() => (
          <RenderWithCondition condition={!isEdit}>
            <Box>
              <Button
                startIcon={<Add />}
                onClick={() => {
                  handleAddRow({ ...stagesConstructorFormRow });
                }}
              >
                Add stage
              </Button>
            </Box>
          </RenderWithCondition>
        )}
      />
    </FormProvider>
  );
};
